import { useState } from 'react';
import { Tab } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';
import { usePrefetchMapLibrary } from '../../hooks/usePrefetchMapLibrary';
import toast from 'react-hot-toast';
import PlaceOnMapModal from './PlaceOnMapModal';

const LOCATION_TYPES = ['BRANCH', 'MAIN_OFFICE', 'OPERATIONS_CENTER', 'OTHER', 'LPO', 'DPO', 'ATM', 'ITM'];

/**
 * Shared branches UI used by:
 *  - the FLD R12 modal entry point (BranchImportModal wraps this in a Dialog)
 *  - the standalone /branches page (BranchesPage)
 *  - the Institution Setup wizard branches step
 *
 * `onClose` is only passed when rendered inside a modal/wizard; on the
 * standalone page we omit it and stay put after a successful import.
 */
export default function BranchesPanel({ onClose }: { onClose?: () => void }) {
  const queryClient = useQueryClient();

  return (
    <Tab.Group>
      <Tab.List className="flex space-x-1 rounded-lg bg-gray-100 p-1 mb-4">
        {['Manage', 'FDIC BankFind', 'Branch & LPO Template', 'CSV Upload'].map(label => (
          <Tab key={label} className={({ selected }) =>
            `w-full rounded-md py-2 text-sm font-medium transition-colors ${
              selected ? 'bg-white text-ocean-700 shadow' : 'text-gray-500 hover:text-gray-700'
            }`
          }>
            {label}
          </Tab>
        ))}
      </Tab.List>
      <Tab.Panels>
        <Tab.Panel><ManageBranchesPanel queryClient={queryClient} /></Tab.Panel>
        <Tab.Panel><FdicImportPanel queryClient={queryClient} onClose={onClose} /></Tab.Panel>
        <Tab.Panel><WallaceImportPanel queryClient={queryClient} onClose={onClose} /></Tab.Panel>
        <Tab.Panel><CsvImportPanel queryClient={queryClient} onClose={onClose} /></Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}

function ManageBranchesPanel({ queryClient }: { queryClient: any }) {
  const { teamId } = useTeamId();
  usePrefetchMapLibrary();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [placingId, setPlacingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['branches', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.getBranches(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fairLendingApi.deleteBranch(teamId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch-aa-validation'] });
      queryClient.invalidateQueries({ queryKey: ['fld-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
      toast.success('Branch deleted');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to delete branch'),
  });

  if (isLoading) return <p className="text-sm text-gray-500 py-8 text-center">Loading branches…</p>;

  const branches = data?.branches || [];

  if (branches.length === 0) {
    return (
      <div className="text-center py-8">
        <BuildingOffice2Icon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-gray-600">No branches imported yet. Use one of the import tabs above.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{branches.length} branches</p>
        <button
          onClick={() => {
            if (confirm(`Delete ALL ${branches.length} branches? This cannot be undone.`)) {
              fairLendingApi.clearBranches(teamId!).then(() => {
                queryClient.invalidateQueries({ queryKey: ['branches'] });
                queryClient.invalidateQueries({ queryKey: ['branch-aa-validation'] });
                queryClient.invalidateQueries({ queryKey: ['fld-analysis'] });
                queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
                toast.success('All branches cleared');
              }).catch(() => toast.error('Failed to clear branches'));
            }
          }}
          className="text-xs text-red-600 hover:text-red-700"
        >
          Delete all
        </button>
      </div>

      <div className="max-h-96 overflow-y-auto border rounded-md divide-y">
        {branches.map((b: any) => (
          <div key={b.id} className="px-3 py-2 text-sm flex items-start gap-2 hover:bg-gray-50">
            <BuildingOffice2Icon className={`h-4 w-4 mt-0.5 shrink-0 ${b.isMainOffice ? 'text-ocean-600' : 'text-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {b.branchName}
                {b.isMainOffice && <span className="ml-2 text-xs text-ocean-700 font-semibold">MAIN</span>}
                {!b.isActive && <span className="ml-2 text-xs text-gray-400">(inactive)</span>}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {b.locationType} · {b.address}, {b.city}, {b.state} {b.zip}
                {b.county && ` · ${b.county}`}
              </p>
              {!b.censusTract && (
                <p className="text-xs text-amber-700 mt-0.5">
                  Not located — the address couldn&rsquo;t be found, so this location isn&rsquo;t on the map or in
                  the assessment-area check.
                </p>
              )}
              {b.censusTract && b.locationMethod === 'MAP_PIN' && (
                <p className="text-xs text-gray-400 mt-0.5">Placed on map · tract {b.censusTract}</p>
              )}
            </div>
            {!b.censusTract && (
              <button
                onClick={() => setPlacingId(b.id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-ocean-700 border border-ocean-600 rounded hover:bg-ocean-50"
              >
                <MapPinIcon className="h-3.5 w-3.5" />
                Place on map
              </button>
            )}
            <button
              onClick={() => setEditingId(b.id)}
              className="p-1.5 text-gray-400 hover:text-ocean-600"
              title="Edit"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                if (confirm(`Delete "${b.branchName}"?`)) deleteMutation.mutate(b.id);
              }}
              disabled={deleteMutation.isPending}
              className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {placingId && (() => {
        const b = branches.find((x: any) => x.id === placingId);
        return b ? (
          <PlaceOnMapModal
            branchId={b.id}
            branchName={b.branchName}
            addressLine={`${b.address}, ${b.city}, ${b.state} ${b.zip}`}
            onClose={() => setPlacingId(null)}
          />
        ) : null;
      })()}

      {editingId && (
        <BranchEditModal
          branch={branches.find((b: any) => b.id === editingId)}
          queryClient={queryClient}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}

function BranchEditModal({ branch, queryClient, onClose }: { branch: any; queryClient: any; onClose: () => void }) {
  const { teamId } = useTeamId();
  const [form, setForm] = useState({
    branchName: branch.branchName || '',
    locationType: branch.locationType || 'BRANCH',
    address: branch.address || '',
    city: branch.city || '',
    state: branch.state || '',
    zip: branch.zip || '',
    isMainOffice: !!branch.isMainOffice,
    isActive: branch.isActive !== false,
    acceptsDeposits: !!branch.acceptsDeposits,
    loanOfficerOnsite: !!branch.loanOfficerOnsite,
  });

  const updateMutation = useMutation({
    mutationFn: () => fairLendingApi.updateBranch(teamId!, branch.id, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch-aa-validation'] });
      queryClient.invalidateQueries({ queryKey: ['fld-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
      toast.success('Branch updated');
      onClose();
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update branch'),
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Edit Branch</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input
              value={form.branchName}
              onChange={(e) => setForm({ ...form, branchName: e.target.value })}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.locationType}
              onChange={(e) => setForm({ ...form, locationType: e.target.value })}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              {LOCATION_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
              <input
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                maxLength={2}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">ZIP</label>
            <input
              value={form.zip}
              onChange={(e) => setForm({ ...form, zip: e.target.value })}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isMainOffice}
                onChange={(e) => setForm({ ...form, isMainOffice: e.target.checked })}
                className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
              />
              <span className="text-gray-700">Main office</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
              />
              <span className="text-gray-700">Active</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.acceptsDeposits}
                onChange={(e) => setForm({ ...form, acceptsDeposits: e.target.checked })}
                className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
              />
              <span className="text-gray-700">Accepts deposits</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.loanOfficerOnsite}
                onChange={(e) => setForm({ ...form, loanOfficerOnsite: e.target.checked })}
                className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
              />
              <span className="text-gray-700">Loan officer onsite</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            className="px-3 py-1.5 text-sm font-medium bg-ocean-600 text-white rounded-md hover:bg-ocean-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FdicImportPanel({ queryClient, onClose }: { queryClient: any; onClose?: () => void }) {
  const { teamId } = useTeamId();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCert, setSelectedCert] = useState<number | null>(null);
  const [selectedName, setSelectedName] = useState('');

  const { data: searchResults, isFetching } = useQuery({
    queryKey: ['fdic-search', searchQuery],
    queryFn: async () => {
      const res = await fairLendingApi.searchFdicInstitutions(searchQuery);
      return res.data.institutions || [];
    },
    enabled: searchQuery.length >= 2,
  });

  const importMutation = useMutation({
    mutationFn: (cert: number) => fairLendingApi.importFdicBranches(teamId!, cert),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch-aa-validation'] });
      queryClient.invalidateQueries({ queryKey: ['fld-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
      queryClient.invalidateQueries({ queryKey: ['cra-dashboard'] });
      toast.success(`Imported ${res.data.imported} branches`);
      onClose?.();
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message;
      toast.error(detail ? `FDIC import failed: ${detail}` : 'Failed to import branches from FDIC', { duration: 8000 });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Search Institution Name</label>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="e.g., Bank of Fayette County"
            className="block w-full rounded-md border-gray-300 pl-9 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">Type any words from the bank's name — partial matches work.</p>
      </div>

      {isFetching && <p className="text-sm text-gray-400">Searching FDIC...</p>}

      {searchResults && searchResults.length > 0 && (
        <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
          {searchResults.map((inst: any) => (
            <button
              key={inst.cert}
              onClick={() => { setSelectedCert(inst.cert); setSelectedName(inst.institutionName); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-ocean-50 transition-colors ${
                selectedCert === inst.cert ? 'bg-ocean-50 border-l-2 border-ocean-600' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">{inst.institutionName}</p>
                  <p className="text-xs text-gray-500">
                    {inst.city}, {inst.state} &middot; CERT: {inst.cert}
                    {inst.branchCount ? ` · ${inst.branchCount} offices` : ''}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {searchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
        <p className="text-sm text-gray-500">No institutions found. Try fewer or different words.</p>
      )}

      {selectedCert && (
        <div className="bg-ocean-50 border border-ocean-200 rounded-lg p-3 text-sm">
          <p className="font-medium text-ocean-800">Selected: {selectedName}</p>
          <p className="text-ocean-600 text-xs">CERT: {selectedCert}</p>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        {onClose && (
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
        )}
        <button
          onClick={() => selectedCert && importMutation.mutate(selectedCert)}
          disabled={!selectedCert || importMutation.isPending}
          className="rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {importMutation.isPending ? 'Importing...' : 'Import Branches'}
        </button>
      </div>
    </div>
  );
}

function WallaceImportPanel({ queryClient, onClose }: { queryClient: any; onClose?: () => void }) {
  const { teamId } = useTeamId();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any>(null);

  const importMutation = useMutation({
    mutationFn: (f: File) => fairLendingApi.importWallaceTemplate(teamId!, f),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch-aa-validation'] });
      queryClient.invalidateQueries({ queryKey: ['fld-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
      queryClient.invalidateQueries({ queryKey: ['cra-dashboard'] });
      setResult(res.data);
      toast.success(`Imported ${res.data.imported} branches (${res.data.enriched} enriched)`);
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to import template'),
  });

  const handleDownload = async () => {
    try {
      const res = await fairLendingApi.downloadBranchTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Branch and LPO Template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        <p className="font-medium mb-1">Branch &amp; LPO Template (Wallace 2026-04-26)</p>
        <p>Download the template, fill it in, then upload here. The system will geocode each address and populate assessment area, MSA, county, census tract, CRA income level, and primary-regulator MMCT classification.</p>
      </div>

      <div>
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border border-ocean-600 text-ocean-700 hover:bg-ocean-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Download Template
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Filled Template (.xlsx)</label>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50">
            <ArrowUpTrayIcon className="h-4 w-4" />
            Choose File
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }}
              className="hidden"
            />
          </label>
          {file && <span className="text-sm text-gray-600">{file.name}</span>}
        </div>
      </div>

      {result && (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md bg-green-50 border border-green-200 p-2">
              <p className="text-xl font-semibold text-green-700">{result.imported}</p>
              <p className="text-xs text-green-600">Imported</p>
            </div>
            <div className="rounded-md bg-blue-50 border border-blue-200 p-2">
              <p className="text-xl font-semibold text-blue-700">{result.enriched}</p>
              <p className="text-xs text-blue-600">Geocoded</p>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-2">
              <p className="text-xl font-semibold text-amber-700">{result.skipped}</p>
              <p className="text-xs text-amber-600">Skipped</p>
            </div>
          </div>
          <div className="border rounded-md divide-y">
            {result.rows.map((r: any, i: number) => (
              <div key={i} className="px-3 py-2 text-sm flex items-start gap-2">
                {r.status === 'imported' && <CheckCircleIcon className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />}
                {r.status === 'imported_unenriched' && <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />}
                {r.status === 'skipped' && <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{r.branchName}</p>
                  {r.enrichment && (
                    <p className="text-xs text-gray-500">
                      {r.enrichment.assessmentAreaName ? `${r.enrichment.assessmentAreaName} · ` : ''}
                      {r.enrichment.countyName}, {r.enrichment.stateName}
                      {r.enrichment.craIncomeLevel ? ` · ${r.enrichment.craIncomeLevel}` : ''}
                      {r.enrichment.isMmct ? ` · ${r.enrichment.mmctRegulator}` : ''}
                    </p>
                  )}
                  {r.status === 'imported_unenriched' && (
                    <p className="text-xs text-amber-600">Imported, but the address couldn&rsquo;t be found. Use Place on map on the Manage tab.</p>
                  )}
                  {r.validationErrors?.length > 0 && (
                    <p className="text-xs text-red-600">{r.validationErrors.join('; ')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        {onClose && (
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Close</button>
        )}
        <button
          onClick={() => file && importMutation.mutate(file)}
          disabled={!file || importMutation.isPending}
          className="rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {importMutation.isPending ? 'Importing & Enriching...' : 'Import & Enrich'}
        </button>
      </div>
    </div>
  );
}

function CsvImportPanel({ queryClient, onClose }: { queryClient: any; onClose?: () => void }) {
  const { teamId } = useTeamId();
  const [csvContent, setCsvContent] = useState('');
  const [fileName, setFileName] = useState('');

  const importMutation = useMutation({
    mutationFn: (csv: string) => fairLendingApi.importCsvBranches(teamId!, csv),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch-aa-validation'] });
      queryClient.invalidateQueries({ queryKey: ['fld-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['cra-assessment-areas'] });
      queryClient.invalidateQueries({ queryKey: ['cra-dashboard'] });
      // A Branch & LPO Template saved as CSV returns the template importer's
      // counts; say how many addresses could not be located instead of hiding it.
      const { imported, enriched, skipped } = res.data;
      if (typeof enriched === 'number' && (imported - enriched > 0 || skipped > 0)) {
        const parts = [`Imported ${imported} locations`];
        if (imported - enriched > 0) parts.push(`${imported - enriched} could not be located — use Place on map on the Manage tab`);
        if (skipped > 0) parts.push(`${skipped} rows skipped for missing required fields`);
        toast(parts.join('; '), { icon: '⚠️', duration: 8000 });
      } else {
        toast.success(`Imported ${imported} branches`);
      }
      onClose?.();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to parse CSV'),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => { setCsvContent(reader.result as string); };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
        <p className="font-medium text-gray-700 mb-1">Required Columns:</p>
        <p>Branch Name, Address, City, State, ZIP</p>
        <p className="mt-1 font-medium text-gray-700">Optional Columns:</p>
        <p>County, Latitude, Longitude, Census Tract, Main Office</p>
        <p className="mt-2 text-gray-500">For richer data + auto-enrichment, use the Branch &amp; LPO Template tab instead.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload CSV File</label>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50">
            <ArrowUpTrayIcon className="h-4 w-4" />
            Choose File
            <input type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
          </label>
          {fileName && <span className="text-sm text-gray-600">{fileName}</span>}
        </div>
      </div>

      {csvContent && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          File loaded: {csvContent.split('\n').filter(l => l.trim()).length - 1} rows detected
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        {onClose && (
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
        )}
        <button
          onClick={() => importMutation.mutate(csvContent)}
          disabled={!csvContent || importMutation.isPending}
          className="rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {importMutation.isPending ? 'Importing...' : 'Import CSV'}
        </button>
      </div>
    </div>
  );
}
